/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useMemo } from 'react';
import ReactSelect, { components, NoticeProps, StylesConfig } from 'react-select';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import classNames from 'classnames';

import Icon from './Icon';
import InvalidFeedback from './InvalidFeedback';
import useInput from '../hooks/useInput';
import { useTranslation } from "../hooks";


export interface FormikSelectOption {
  id?: string;
  label?: string;
  name: string;
  options: {
    label: string;
    value: any;
  }[];
  placeholder?: string;
  loading?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
  icon?: string;
  swap?: boolean;
  iconProps?: React.HTMLAttributes<HTMLSpanElement>; // Assuming icon is rendered as a span
  className?: string;
  resetFields?: string[]; // Clarify expected type if different
  isModal?: boolean;
  getOptionLabel?: (option: { label: string; value: any }) => string; // More specific type signature if known
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: any) => void; // Include selected option for clarity
}

const NoOptionsMessage = (props: NoticeProps) => {
  const {t} = useTranslation();
  return (
    <components.NoOptionsMessage {...props}>
      {t('No options')}
    </components.NoOptionsMessage>
  );
};


const styles: StylesConfig = {
  multiValue: (base, state) => {
    // @ts-ignore
    return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base;
  },
  multiValueLabel: (base, state) => {
    // @ts-ignore
    return state.data.isFixed
      ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 }
      : base;
  },
  multiValueRemove: (base, state) => {
    // @ts-ignore
    return state.data.isFixed ? { ...base, display: 'none' } : base;
  },
};


export function FormikSelect({
  id: defaultId,
  label,
  name,
  options = [],
  placeholder = '',
  loading = false,
  isMulti = false,
  isClearable = true,
  closeMenuOnSelect = true,
  className,
  icon,
  swap = false,
  iconProps = {},
  // resetFields = [],
  isModal,
  getOptionLabel,
  disabled,
  required,
  onChange = () => {
    return false;
  }
}: FormikSelectOption) {
  const [field, meta, helpers] = useField(name);
  const { id, idIcon, idFeedback, ariaDescribedby } = useInput(defaultId, label, icon);

  const handleChange = useCallback(
    (element) => {
      if (isMulti) {
        helpers.setValue(element.map((item) => item.value));
        if (onChange) onChange(element);
      } else {
        helpers.setValue(element?.value);
        if (onChange) onChange(element?.value);
      }
    },
    [helpers, isMulti, onChange]
  );

  /*
     // FIXME: This is a workaround to reset the formik form after a select change
     setValues({
     ...values,
     ...resetFields.reduce((cleared, resetField) => ({
     ...cleared,
     ...(values[resetField] && { [resetField]: '' })
     }), {})
     });
     */

  const flatOptions = useMemo(() => {
    // @ts-ignore
    return options.reduce((flatting, option) => {
      // @ts-ignore
      if (option.options) {
        // @ts-ignore
        return [...flatting, ...option.options];
      }
      return [...flatting, option];
    }, []);
  }, [options]);

  const option =
    useMemo(() => {
      if (Array.isArray(field.value)) {
        const indexedValues = field.value.filter(Boolean).reduce(
          (index, item) => ({
            ...index,
            [item.value || item]: item
          }),
          {}
        );

        // @ts-ignore
        return flatOptions.filter((item) => indexedValues[item.value]);
      }
      // @ts-ignore
      return flatOptions.find((element) => element.value === field.value);
    }, [field.value, flatOptions]) || null;

  const Control = useCallback(
    ({ children, className: classControl, ...props }) => (
      <div className="input-group has-validation">
        {icon && swap && (
          <span className="input-group-text" id={idIcon}>
            <Icon icon={icon} {...iconProps} />
          </span>
        )}
        <components.Control
          {...props}
          {...(ariaDescribedby && {
            'aria-describedby': ariaDescribedby
          })}
          className={classNames(
            'form-control',
            meta.touched && meta.error && 'is-invalid z-1',
            meta.touched && !meta.error && 'is-valid',
            classControl
          )}
        >
          {children}
        </components.Control>
        {icon && !swap && (
          <span className="input-group-text" id={idIcon}>
            <Icon icon={icon} {...iconProps} />
          </span>
        )}
        <InvalidFeedback id={idFeedback} message={meta.touched && meta.error ? meta.error : ''} />
      </div>
    ),
    [ariaDescribedby, icon, iconProps, idFeedback, idIcon, meta.error, meta.touched, swap]
  );

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <ReactSelect
        id={id}
        options={options}
        placeholder={placeholder}
        isMulti={isMulti}
        isClearable={isClearable}
        value={option}
        onChange={handleChange}
        isLoading={loading}
        isDisabled={disabled}
        components={{ Control, NoOptionsMessage }}
        className="react-select-container"
        classNamePrefix="react-select"
        closeMenuOnSelect={closeMenuOnSelect}
        // @ts-ignore
        styles={isModal ? { menuPortal: (base) => ({ ...base, boxSizing: "border-box" }) }: styles}
        menuShouldScrollIntoView={isModal && false}
        // @ts-ignore
        menuPlacement={isModal && 'auto'}
        // @ts-ignore
        menuPortalTarget={document.body}
        allowAsProps
        getOptionLabel={getOptionLabel}
        isOptionDisabled={(option) => option.disabled}
      />
    </div>
  );
}

FormikSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  icon: PropTypes.string,
  swap: PropTypes.bool,
  iconProps: PropTypes.object,
  className: PropTypes.string,
  resetFields: PropTypes.array,
  isModal: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func
};

export default FormikSelect;
